import type {
  ClientBuilderPlanGltfTiming,
  ClientBuilderPlanGltfTimingSpan,
} from "api-types/builders/buildersResponses";
import type { Building } from "building-types/building/buildingTypes";
import type { ShowroomFloorPlanPayload } from "building-types/building/showroomFloorPlanPayloadTypes";
import type { LevelInfo } from "building-types/building/showroomTypes";
import type { ShowroomBuildingStats } from "building-types/building/showroomTypes";
import type { Doc } from "building-types/doc";
import type { EntityIntegerSource } from "building-types/doc/entityIntegerSource";
import { BuildingViewerViewMode } from "building-types/geometry/buildingGeometryTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type {
  SerializedOptionSetEntityConnectedTable,
  SerializedOptionSetEntityDisconnectedTable,
} from "building-types/options/optionSetEntityRelationsTypes";
import type { Table } from "common/data/table";
import { OS, getOS } from "common/utils/context";
import type { InstanceIdPath } from "doc/options/componentInstanceTree";
import type { OptionSetStateSet } from "doc/options/optionSetState";
import { NullEntityIntegerSource } from "doc/utils/nullEntityIntegerSource";
import { EnvPreset } from "gfx/EnvPreset";
import type { BuildingSummaryForPaletteThumbnail } from "gfx/gltf";
import type { GLTF } from "gfx/threejs/examples/jsm/loaders/GLTFLoader";
import type { BuildingViewerSceneState } from "lib/house/render/buildingViewerSceneState";
import { SiteType, defaultSceneState } from "lib/house/render/buildingViewerSceneState";
import { defaultShowroomView3DFilter } from "lib/house/render/viewFilterDefaults";
import type { PricingResults } from "lib/options/calculatePricing";
import type { ModalProps } from "ui/components/Modal";
import type {
  PlanData,
  ShowroomOption,
  ShowroomOptionSet,
  ShowroomPatch,
  ShowroomPlanKey,
  UrlOptionMap,
} from "./showroomTypes";

/** Discriminated union for rendering mode: loading, building (Building object), or gltf (parsed GLTF). */
export type ShowroomRenderingState =
  | { renderMode: "loading" }
  | { renderMode: "building"; building: Building }
  | {
      renderMode: "gltf";
      gltf: GLTF;
      gltfSource: ShowroomGltfSourceMode;
      requestedOptionChoices: OptionChoices | undefined;
      resolvedOptionChoices: OptionChoices;
    };

/** Which GLTF backend is currently active. `null` means main-thread building generation. */
export type ShowroomGltfSourceMode = "worker" | "server";

/**
 * Mode-agnostic timing for a single completed GLTF generation.
 */
export interface ShowroomGltfSourceTiming extends ClientBuilderPlanGltfTiming {
  /** Browser-observed time from generation request to parsed GLTF ready. */
  clientObservedTotal: ClientBuilderPlanGltfTimingSpan;
  /** Browser main-thread GLTF parse time. */
  gltfParse: ClientBuilderPlanGltfTimingSpan;
  /** Server-only fetch/response time before GLTF parsing starts. */
  serverResponse?: ClientBuilderPlanGltfTimingSpan;
}

export interface ShowroomDataState {
  showroomVersion: "intro" | "sales" | "ops" | "design"; // only "intro" is enabled
  doc?: Doc;
  optionChoices: OptionChoices;
  entityIntegerSource: EntityIntegerSource;
  changedOptions: Table<DocOption>;
  planId?: string;
  /** Canonical post-building floor-plan payload consumed by the sheet-geometry 2D viewer. */
  floorPlan: ShowroomFloorPlanPayload | null;
  planKey: ShowroomPlanKey;
  urlOptionMap: UrlOptionMap;
  stats: ShowroomBuildingStats;
  /** Undefined until generation computes it; a zeroed default is indistinguishable from a real $0 total. */
  pricing: PricingResults | undefined;
  // For hotspots
  optionSetsTable: { [optionSetId: string]: ShowroomOptionSet };
  connectedOptionSets: SerializedOptionSetEntityConnectedTable; // filtered to only visible option sets

  buildingState: {
    connectedSets: SerializedOptionSetEntityConnectedTable;
    disconnectedSets: SerializedOptionSetEntityDisconnectedTable;
    optionSetStates: OptionSetStateSet;
  };

  /** Evaluated option sets from building generation. */
  evaluatedOptionSets?: Table<DocOptionSet>;

  /** Building summary for palette thumbnail generation (worker mode). */
  buildingSummary?: BuildingSummaryForPaletteThumbnail;

  /** Active component-instance paths used to filter GLTF-derived option trees. */
  activeInstancePaths: InstanceIdPath[];

  renderingState: ShowroomRenderingState;
  levels: LevelInfo[];
}

export interface ShowroomViewState {
  sceneState: BuildingViewerSceneState;
  selectedLevelId?: string;
  selectedReportingUnitId?: string;
  modal: ModalProps & { modalKey?: string };
  initialBuildingRendered: boolean;
  activeOption?: ShowroomOption;
  viewer: "3d" | "2d";
  heroVisible: boolean;
  /** Secondary-category key currently at the top of the options scroll; drives the nav breadcrumb. */
  activeSecondaryKey?: string;
  // A number from 0 to 1, where 0 = no degradation, and 1 = max.
  // Currently only 0 and 1 are used, but enables more fine-grained control as needed.
  // Related, but not the same as GPU perfTier.
  degradeUILevel: number;
  loadingOption?: { optionId: string; optionSetId: string };
}

// Performance-related state that can be applied to degrade UI
export type ShowroomPerformanceState = Pick<ShowroomState, "degradeUILevel" | "sceneState">;

export interface ShowroomEditState {
  mode: null | "EDIT" | "PREVIEW";
  // Stores the args for each patch
  patches: ShowroomPatch[];
  // Used to traverse patches via undo/redo
  currentPatch: number;
  // Updated whenever plan is saved. Needed to maintain which version to apply patches to
  versions: PlanData[];
  currentVersion: number;
  patchedPlanData: PlanData | null;
  massEditOptionSets: ShowroomOptionSet[];
}

export interface ShowroomCrashHandlerState {
  crashHandlerStateLoaded: boolean;
  disable3dViewer: boolean;
  /** Increments each time a building is generated. Used by crash handler to detect new renders. */
  buildingGenerationCount: number;
}

export type ShowroomState = ShowroomDataState & ShowroomViewState & ShowroomEditState & ShowroomCrashHandlerState;

export const defaultShowroomDataState: ShowroomDataState = {
  showroomVersion: "intro", // TODO: hydrate from url
  optionChoices: {},
  entityIntegerSource: NullEntityIntegerSource,
  doc: undefined,
  planId: undefined,
  floorPlan: null,
  planKey: "communityPlan",
  // Keep the key present so `...defaultShowroomState` still clears a previous plan's price on reset.
  pricing: undefined,
  changedOptions: {},
  urlOptionMap: { optionKeyToHash: {}, hashToOption: {} },
  optionSetsTable: {},
  stats: {
    beds: 0,
    baths: 0,
    sqft: 0,
    garageCount: 0,
  },
  connectedOptionSets: {},
  buildingState: {
    connectedSets: {},
    disconnectedSets: {},
    optionSetStates: {},
  },
  evaluatedOptionSets: undefined,
  buildingSummary: undefined,
  activeInstancePaths: [],

  // Rendering state
  renderingState: { renderMode: "loading" },
  levels: [],
};

// Mobile devices start degraded by default (conservative approach).
// deriveShowroomPerformanceState can un-degrade them if performance metrics show the device is capable.
// This is better UX than starting full and downgrading (which would be jarring).
const degradeUILevels = {
  [OS.IPHONE]: 1, // full degradation
  [OS.ANDROID]: 1, // full degradation
  [OS.IPAD]: 0.1, // minimal, ie. will run crash handler
};

const degradeUILevel = degradeUILevels[getOS() ?? ""] ?? 0;

export const defaultUILevelSceneState = {
  siteType: SiteType.FALLBACK_TO_GRASS,
  envPreset: EnvPreset.MORNING,
};

export const degradedSceneState = {
  siteType: SiteType.GROUND_PLANE_DISABLE_SITE,
  envPreset: EnvPreset.WHITEBOX,
};

/**
 * Returns the scene state patches to apply for a given degradation level.
 * Encapsulates the mapping between degradeUILevel and scene state settings.
 */
export function getSceneStateForDegradeLevel(
  level: number,
  prevSceneState: BuildingViewerSceneState,
): BuildingViewerSceneState {
  return level === 1
    ? { ...prevSceneState, ...degradedSceneState }
    : { ...prevSceneState, ...defaultUILevelSceneState };
}

export const defaultShowroomViewState: ShowroomViewState = {
  degradeUILevel,
  sceneState: {
    ...defaultSceneState,
    viewMode: BuildingViewerViewMode.EXTERIOR,
    ...(degradeUILevel === 1 ? degradedSceneState : defaultUILevelSceneState),
    exteriorLightProbesEnabled: false,
    resetCameraOnLevelChange: true,
    viewFilter: {
      ...defaultShowroomView3DFilter,
      exteriorLightProbes: false,
      interiorLightProbes: false,
    },
  },
  selectedLevelId: undefined,
  selectedReportingUnitId: undefined,
  modal: {
    open: false,
    size: "small",
  },
  initialBuildingRendered: false,
  activeOption: undefined,
  viewer: "3d",
  heroVisible: true,
};

export const defaultShowroomEditState: ShowroomEditState = {
  mode: null,
  patches: [],
  currentPatch: -1,
  versions: [],
  currentVersion: -1,
  patchedPlanData: null,
  massEditOptionSets: [],
};

export const defaultShowroomCrashHandlerState: ShowroomCrashHandlerState = {
  crashHandlerStateLoaded: false,
  disable3dViewer: false,
  buildingGenerationCount: 0,
};

export const defaultShowroomState: ShowroomState = {
  ...defaultShowroomDataState,
  ...defaultShowroomViewState,
  ...defaultShowroomEditState,
  ...defaultShowroomCrashHandlerState,
};
